import React from 'react';
import {
  ChatGPTLogo,
  ClaudeLogo,
  ClaudeCodeLogo,
  VSCodeLogo,
  CursorLogo,
  OtherToolLogo,
} from './mcp-tool-logos';

export const GLOBAL_MCP_URL = 'https://mcp.saleshandy.com/mcp';

export type ToolConfig = {
  id: string;
  name: string;
  description: string;
  icon: React.FC;
  iconBgClass: string;
  authType: 'oauth' | 'api-key';
  externalUrl?: string;
  prerequisites?: string;
  steps: string[];
};

export const MCP_TOOLS: ToolConfig[] = [
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    description: 'Pro/Team/Enterprise account',
    icon: ChatGPTLogo,
    iconBgClass: 'mcp-tool-icon--chatgpt',

    authType: 'oauth',
    externalUrl: 'https://chatgpt.com/#settings/Connectors',
    prerequisites: 'ChatGPT Plus/Team account. Developer Mode enabled.',
    steps: [
      'Enable Developer Mode: Settings → Apps & Connectors → Advanced → Toggle ON',
      'Go to Settings → Apps & Connectors → Click "Create App"',
      `Fill form: Name: Saleshandy, URL: ${GLOBAL_MCP_URL}, Auth: OAuth`,
      'Click "Create" → Browser opens for authorization',
      'Log in to Saleshandy and click "Authorize"',
      'Auto-redirect to ChatGPT → Connection complete',
    ],
  },
  {
    id: 'claude-web',
    name: 'Claude',
    description: 'Direct browser integration',
    icon: ClaudeLogo,
    iconBgClass: 'mcp-tool-icon--claude-web',

    authType: 'oauth',
    externalUrl:
      'https://claude.ai/settings/connectors?modal=add-custom-connector',
    prerequisites: 'Active Claude account (Pro).',
    steps: [
      'Go to Settings → Connectors',
      'Click "Add custom connector"',
      `Paste MCP URL: ${GLOBAL_MCP_URL}`,
      'Browser redirects to Saleshandy authorization page',
      'Log in and click "Authorize"',
      'Auto-redirect back to Claude → Connection active',
    ],
  },
  {
    id: 'claude-code',
    name: 'Claude Code (CLI)',
    description: 'For terminal-based users',
    icon: ClaudeCodeLogo,
    iconBgClass: 'mcp-tool-icon--claude-code',

    authType: 'oauth',
    prerequisites: 'Claude Code CLI installed on your machine.',
    steps: [
      'Open your terminal app',
      `Run: claude mcp add --transport http saleshandy ${GLOBAL_MCP_URL}`,
      'Browser opens automatically for authorization',
      'Log in to Saleshandy and click "Authorize"',
      'Return to terminal → Connection confirmed',
      'Verify with: claude mcp list',
    ],
  },
  {
    id: 'vscode',
    name: 'VS Code',
    description: 'Integrated Development Environment',
    icon: VSCodeLogo,
    iconBgClass: 'mcp-tool-icon--vscode',

    authType: 'oauth',
    prerequisites: 'VS Code with an MCP-compatible extension installed.',
    steps: [
      'Press Cmd+Shift+P (Mac) or Ctrl+Shift+P (Win)',
      'Type "MCP: Add Server" and select it',
      'Select transport type: HTTP',
      `Paste URL: ${GLOBAL_MCP_URL}`,
      'Enter server name: Saleshandy',
      'Browser opens → Log in and authorize',
      'Return to VS Code → Connection established',
    ],
  },
  {
    id: 'cursor',
    name: 'Cursor IDE',
    description: 'AI-first coding + outreach',
    icon: CursorLogo,
    iconBgClass: 'mcp-tool-icon--cursor',

    authType: 'oauth',
    prerequisites: 'Cursor IDE installed.',
    steps: [
      'Open Settings (Cmd+, or Ctrl+,)',
      'Go to Tools & Integration → MCP Tools',
      'Click "Add Custom MCP" and name it "Saleshandy"',
      `Set URL to: ${GLOBAL_MCP_URL}`,
      'Save and reload Cursor',
      'Browser opens → Authorize with Saleshandy',
      'Return to Cursor → Connected',
    ],
  },
  {
    id: 'other',
    name: 'Other Tool',
    description: 'Generic MCP configuration',
    icon: OtherToolLogo,
    iconBgClass: 'mcp-tool-icon--other',

    authType: 'api-key',
    prerequisites: 'Any MCP-compatible client.',
    steps: [
      "Open your tool's settings/configuration",
      'Look for: Integrations, Connectors, or MCP Servers',
      `Add new MCP server with URL: ${GLOBAL_MCP_URL}`,
      'Browser opens → Authorize → Done',
      'If OAuth is not supported, generate an API key in Saleshandy instead.',
    ],
  },
];
